import React, { useEffect, useRef, useMemo, ReactElement } from "react";
import WidgetCard from "./WidgetCard";
import { IRevstackLayoutFilters, RevstackFilterTypes, WidgetType } from "@ts/common-types";
import { w4WidgetFormatter } from "@/ts/revstack/formatters";
import { useSelector } from "react-redux";
import { buildWidgetCardFilters } from "@/ts/revstack/ui-utils";
import { renderWidgetStructure } from "@/ts/revstack/renderers";

interface IRevstackSovDistributionWidgetProps {
    widgetConfig: any;
    widgetData: any;
    widgets: any;
    title: string;
    isTitleRequired?: boolean;
    isCardRequired?: boolean;
    icon?: ReactElement;
}

const RevstackSovDistributionWidget: React.FC<
    IRevstackSovDistributionWidgetProps
> = ({
    widgetConfig,
    widgetData,
    widgets,
    title,
    isTitleRequired = true,
    isCardRequired = true,
    icon
}) => {
        const revstackLayoutFilters = useSelector(
            (state: { revstackLayoutFilters: { value: IRevstackLayoutFilters } }) => state.revstackLayoutFilters.value
        );
        const chartContainerRef = useRef<HTMLDivElement>(null);
        const chartComponentRef = useRef<any>(null);
        // Helper function to get metric label
        const getMetricLabel = useMemo(() => {
            const metricType = revstackLayoutFilters?.metricType;
            if (!metricType) return "";

            const selectedOption = metricType.options?.find(
                (option: any) => option.value === metricType.selected
            );
            return selectedOption?.label || "";
        }, [revstackLayoutFilters?.metricType]);

        useEffect(() => {
            if (!chartContainerRef.current || !chartComponentRef.current) return;
            const observer = new window.ResizeObserver(() => {
                if (chartComponentRef.current && chartComponentRef.current.chart) {
                    chartComponentRef.current.chart.reflow();
                }
            });
            observer.observe(chartContainerRef.current);
            return () => observer.disconnect();
        }, []);

        return (
            <WidgetCard
                overwriteDefaultClass={!isCardRequired}
                className={
                    !isCardRequired
                        ? "bg-transparent border-0 rounded-0 shadow-none"
                        : ""
                }
                style={{ height: "350px" }}
            >
                {isTitleRequired && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3"
                        >
                            <div className="d-flex gap-1 align-items-center">
                                <span
                                    className="icon-btn"
                                    style={{
                                        marginRight: 10,
                                    }}
                                >
                                   {icon}
                                </span>
                                <div className="d-flex flex-column justify-content-start">
                                    <h4 className="mb-0">
                                        {title || "Category Performance"}
                                    </h4>
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                {
                                    widgetConfig?.config?.isTrendsButtonRequired ? (
                                buildWidgetCardFilters({
                                    filters: [
                                        {
                                            type: RevstackFilterTypes.TRENDS,
                                            section: "",
                                            disabled: true,
                                            isComingSoon: true
                                        }
                                    ]
                                })
                                ) : ""}
                            </div>
                        </div>
                    </div>

                )}
                <div ref={chartContainerRef} className="w-100 h-100">
                    {renderWidgetStructure(widgets, (widgetList: any, widgetIdx: number) => {
                        const shouldShowWidget = widgetList.type === WidgetType.W4;

                        if (!shouldShowWidget) return null;

                        //!Note[DND]: this should be dynamic this case have to be handled in config
                        const currentChartData = w4WidgetFormatter(widgetList, widgetData);

                        if (!widgetList.widgetComponent) {
                            console.warn(
                                `Widget component not found for type: ${widgetList.type}`
                            );
                            return null;
                        }

                        const WidgetComponent = widgetList.widgetComponent;

                        return (
                            <WidgetComponent
                                widget={widgetList}
                                key={`${widgetList.type}-${widgetIdx}`}
                                chartComponentRef={chartComponentRef}
                                widgetData={currentChartData}
                                graphColors={currentChartData?.graphColors}
                                isLoading={currentChartData?.isLoading}
                                config={widgetList?.config}
                            />
                        );
                    })}
                </div>
            </WidgetCard >
        );
    };

export default RevstackSovDistributionWidget;
